import { formatARS, type PaymentMethod } from '@gl/shared';
import { useMutation } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Lock, ReceiptText, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { errorMessage } from '@/lib/api';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { toSaleLines, useCart } from './cart';
import { useMediaQuery } from './useMediaQuery';
import { CartPanel } from './CartPanel';
import { CashTodaySummary } from './CashTodaySummary';
import { formatBusinessDate } from './format';
import { ProductGrid } from './ProductGrid';
import { caja, useActiveCatalog, useCashToday, useInvalidateCaja } from './queries';
import { MovementDialog, TodayMovements, TodaySales } from './TodayActivity';

export function CajaPage() {
  const local = useCurrentLocal();
  const isManager = useHasRole('OWNER', 'MANAGER');
  const today = useCashToday(local.id);
  const catalog = useActiveCatalog(local.id);
  const invalidate = useInvalidateCaja(local.id);
  const cart = useCart();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('EFECTIVO');
  const [cartOpen, setCartOpen] = useState(false);
  const [movement, setMovement] = useState<'expense' | 'income' | null>(null);
  const [tab, setTab] = useState('vender');
  const closed = !!today.data?.isClosed;
  const desktop = useMediaQuery('(min-width: 1024px)');

  const sell = useMutation({
    mutationFn: () =>
      caja.createSale(local.id, {
        items: toSaleLines(cart.lines),
        paymentMethod,
        discountCents: cart.discountCents,
      }),
    onSuccess: (sale) => {
      toast.success(`Venta #${sale.number} · ${formatARS(sale.totalCents)}`);
      cart.dispatch({ type: 'clear' });
      setPaymentMethod('EFECTIVO');
      setCartOpen(false);
      void invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const panel = (
    <CartPanel
      cart={cart}
      paymentMethod={paymentMethod}
      onPaymentMethod={setPaymentMethod}
      onConfirm={() => sell.mutate()}
      pending={sell.isPending}
      disabled={closed}
    />
  );

  return (
    <div>
      <PageHeader
        title="Caja"
        description={today.data ? `Día de caja: ${formatBusinessDate(today.data.businessDate)}` : undefined}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setMovement('expense')} disabled={closed}>
              <ArrowUpRight /> Gasto
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMovement('income')} disabled={closed}>
              <ArrowDownLeft /> Ingreso
            </Button>
            {isManager && (
              <Button variant="outline" size="sm" asChild>
                <Link to="cierres">
                  <ReceiptText /> Cierre
                </Link>
              </Button>
            )}
          </>
        }
      />

      <CashTodaySummary data={today.data} className="mb-4" />

      {closed && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          <Lock className="size-4 shrink-0" />
          La caja de hoy ya está cerrada. Las ventas nuevas se habilitan cuando empiece el próximo día de caja.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full md:w-fit">
          <TabsTrigger value="vender" className="flex-1 md:flex-none">
            Vender
          </TabsTrigger>
          <TabsTrigger value="hoy" className="flex-1 md:flex-none">
            Movimientos de hoy
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vender" className="mt-2">
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-6">
            <ProductGrid
              categories={catalog.data?.categories ?? []}
              loading={catalog.isLoading}
              disabled={closed}
              canCustom={isManager}
              onAdd={(line) => cart.dispatch({ type: 'add', line })}
            />
            {desktop && (
              <aside className="sticky top-6 flex max-h-[calc(100dvh-3rem)] flex-col rounded-xl border bg-card p-4">
                <h2 className="mb-2 font-semibold">Carrito</h2>
                {panel}
              </aside>
            )}
          </div>
          {cart.count > 0 && <div className="h-20 lg:hidden" aria-hidden />}
          {catalog.isError && (
            <p className="mt-4 text-sm text-destructive">No pudimos cargar el menú: {errorMessage(catalog.error)}</p>
          )}
        </TabsContent>

        <TabsContent value="hoy" className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
          {today.data && (
            <>
              <TodaySales businessDate={today.data.businessDate} onSell={closed ? undefined : () => setTab('vender')} />
              <TodayMovements businessDate={today.data.businessDate} onAdd={closed ? undefined : setMovement} />
            </>
          )}
        </TabsContent>
      </Tabs>

      {tab === 'vender' && cart.count > 0 && (
        <div className="fixed inset-x-0 bottom-[calc(4.25rem+env(safe-area-inset-bottom))] z-30 px-4 md:bottom-6 md:left-auto md:w-96 lg:hidden">
          <Button size="lg" className="h-14 w-full justify-between text-base shadow-lg" onClick={() => setCartOpen(true)}>
            <span className="flex items-center gap-2">
              <ShoppingCart /> Ver carrito ({cart.count})
            </span>
            <span className="tabular-nums">{formatARS(cart.totalCents)}</span>
          </Button>
        </div>
      )}

      <Sheet open={cartOpen && !desktop} onOpenChange={setCartOpen}>
        <SheetContent side="bottom" className="flex h-[85dvh] flex-col gap-0 lg:hidden">
          <SheetHeader className="pb-2">
            <SheetTitle>Carrito</SheetTitle>
            <SheetDescription className="sr-only">Productos de la venta, descuento y método de pago</SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">{panel}</div>
        </SheetContent>
      </Sheet>

      <MovementDialog kind={movement} onOpenChange={(o) => !o && setMovement(null)} />
    </div>
  );
}
